import { useState, useMemo, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import { Option } from '@/shared/components/ui/SearchableSelect';
import { apiRoutes } from "@/shared/api/routes";

interface ServiceAreasApiResponse {
  data: (string | number | boolean | null)[][];
  info: {
    limit: number;
    results: number;
    total_results: number;
    elapsed_total: number;
    elapsed_per_item: number;
    db_query: string;
    select_query: boolean;
  };
}

export const useServiceAreasWithPagination = (enabled: boolean = true) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch service areas with pagination and search
  const {
    data: serviceAreasData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['serviceAreas', 'pagination', searchTerm],
    queryFn: async ({ pageParam = 0 }) => {
      const query = searchTerm
        ? [['like', 'enclosure.name', `%${searchTerm}%`]]
        : [];
      
      const offset = pageParam * 10;
      
      const requestBody = {
        select: ['id', 'name', 'enclosure.name'],
        query,
        options: {
          timeout: 10,
          offset,
          limit: 10,
          order: [['enclosure.name', 'asc_nulls_last']],
          calculate_total: true,
          return_results: true,
          select_distinct: true,
        },
      };

      const response = await apiClient.post<ServiceAreasApiResponse>(
        apiRoutes.proxy('support/internal/service_area/select_query'),
        requestBody
      );

      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce((sum, page) => sum + (page.data?.length || 0), 0);
      return loadedItems < (lastPage.info?.total_results || 0) ? allPages.length : undefined;
    },
    enabled,
    initialPageParam: 0,
  });

  // Transform service areas for SearchableSelect
  const serviceAreaOptions = useMemo<Option[]>(() => {
    if (!serviceAreasData?.pages) return [];
    return serviceAreasData.pages.flatMap((page) =>
      (page.data || []).map((area: (string | number | boolean | null)[]) => ({
        id: area[0]?.toString() || '',
        name: String(area[2] || area[1] || 'Unknown Service Area'), // enclosure.name, fallback to name
      }))
    );
  }, [serviceAreasData]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearchChange = useCallback((search: string) => {
    setSearchTerm(search);
  }, []);

  return {
    serviceAreaOptions,
    handleLoadMore,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    handleSearchChange,
  };
};